// Shared building blocks for the simplified staff (matchmaker / coach / admin)
// pages. One look everywhere: a page header that says what the page is for,
// a short row of key numbers, clear sections, friendly empty states and an
// optional "这是什么？" explainer — so a new matchmaker can understand a page
// without being told.
//
// Styled in the member-side "Luxe" language (theme/luxe.js): ivory ground,
// ink type, serif display titles / big numbers, letter-spaced small labels,
// hairline rules, square corners, no shadows. The staff shell
// (DashboardLayout, class `staff-luxe`) remaps the legacy CSS variables to the
// same palette, so these sit naturally next to class-styled pages.
import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { L } from '../../theme/luxe';

// Key names are kept from the old staff palette so every caller keeps working.
export const S = {
  ink: L.ink,                 // #111111 primary type
  ink2: L.ink2,               // #3A3835 secondary type
  sub: '#6E685F',             // small / secondary text (never lighter than this)
  mute: '#6E685F',
  line: 'rgba(17,17,17,.12)', // hairline
  lineSoft: 'rgba(17,17,17,.08)',
  soft: L.paper2,             // #EFEAE2 recessed panel
  paper: L.paper,             // #F6F3EE ground
  card: L.card,               // #FFFDF9 raised surface
  brand: L.ink,               // was burgundy — the Luxe accent is ink
  gold: L.goldDeep,           // #8A6D3B gold for text
  goldDecor: L.gold,          // #B8975A gold for rules / icons only
  ok: L.online,               // #3F7D5A
  warn: '#93591A',
  bad: L.danger,              // #9E2B25
  serif: L.serif,
  sans: L.sans,
  radius: 2,
  shadow: 'none',
};

const smallCaps = { fontFamily: S.sans, fontSize: 11.5, fontWeight: 400, letterSpacing: '.22em', color: S.sub };

// Page title + one plain-language sentence about what the page is for, with
// the page's main actions on the right. `kicker` (optional) is the small gold
// letter-spaced line above the title. `help` (optional) adds a collapsible
// "这是什么？/ How this works" panel under the header.
export const PageHeader = ({ title, subtitle, actions, help, helpTitle = '这是什么？怎么用？', kicker }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', borderBottom: `1px solid ${S.ink}`, paddingBottom: 20 }}>
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {kicker && <span style={{ fontFamily: S.sans, fontSize: 12, letterSpacing: '.26em', color: S.gold, textTransform: 'uppercase' }}>{kicker}</span>}
          <h1 style={{ margin: 0, fontFamily: S.serif, fontSize: 'clamp(32px, 4.4vw, 48px)', fontWeight: 500, lineHeight: 1.05, color: S.ink, letterSpacing: '-.01em' }}>{title}</h1>
          {subtitle && <p style={{ margin: '4px 0 0', fontSize: 14, color: S.sub, lineHeight: 1.65, maxWidth: 720 }}>{subtitle}</p>}
        </div>
        {actions && <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>{actions}</div>}
      </div>
      {help && (
        <div style={{ marginTop: 14 }}>
          <button type="button" onClick={() => setOpen((o) => !o)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 0, padding: 0, cursor: 'pointer', color: S.gold, fontFamily: S.sans, fontSize: 12.5, letterSpacing: '.14em' }}>
            <HelpCircle size={15} strokeWidth={1.5} /> {helpTitle}
            <ChevronDown size={14} strokeWidth={1.5} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
          </button>
          {open && (
            <div style={{ marginTop: 10, padding: '16px 18px', background: S.soft, borderLeft: `1px solid ${S.goldDecor}`, fontSize: 13.5, color: S.ink2, lineHeight: 1.8 }}>
              {help}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// A row of 2–5 key numbers. items: [{ label, value, hint?, tone?: 'ok'|'warn'|'bad'|'brand' , icon? }]
// Serif numbers over letter-spaced labels, separated by hairlines (no cards).
// The inner grid is shifted 1px left inside a clipping box, so the left
// hairline of the first cell in every wrapped row is hidden.
export const StatStrip = ({ items = [], style }) => (
  <div style={{ overflow: 'hidden', borderBottom: `1px solid ${S.line}`, marginBottom: 28, ...style }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', marginLeft: -1 }}>
      {items.filter(Boolean).map((it, i) => {
        const color = it.tone === 'ok' ? S.ok : it.tone === 'warn' ? S.warn : it.tone === 'bad' ? S.bad : it.tone === 'brand' ? S.gold : S.ink;
        const Icon = it.icon;
        return (
          <div key={i} style={{ borderLeft: `1px solid ${S.line}`, padding: '6px 22px 22px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, ...smallCaps }}>
              {Icon && <Icon size={13} strokeWidth={1.5} />}{it.label}
            </div>
            <div style={{ fontFamily: S.serif, fontSize: 42, fontWeight: 500, color, lineHeight: 1, wordBreak: 'break-word' }}>{it.value}</div>
            {it.hint && <div style={{ fontSize: 12, color: S.sub, lineHeight: 1.5 }}>{it.hint}</div>}
          </div>
        );
      })}
    </div>
  </div>
);

// A titled block of content. Keeps every page's sections looking the same.
export const Section = ({ title, description, actions, children, style, bodyStyle }) => (
  <section style={{ background: S.card, border: `1px solid ${S.lineSoft}`, borderRadius: S.radius, marginBottom: 24, ...style }}>
    {(title || actions) && (
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, padding: '18px 22px 14px', borderBottom: `1px solid ${S.line}`, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          {title && <h2 style={{ margin: 0, fontFamily: S.serif, fontSize: 24, fontWeight: 500, lineHeight: 1.2, color: S.ink }}>{title}</h2>}
          {description && <p style={{ margin: '4px 0 0', fontSize: 13, color: S.sub, lineHeight: 1.6 }}>{description}</p>}
        </div>
        {actions && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>{actions}</div>}
      </div>
    )}
    <div style={{ padding: 22, ...bodyStyle }}>{children}</div>
  </section>
);

// What to show when a list is empty: say why, and what to do next.
export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div style={{ textAlign: 'center', padding: '44px 20px', color: S.sub }}>
    {Icon && <div style={{ margin: '0 auto 14px', display: 'flex', justifyContent: 'center', color: S.goldDecor }}><Icon size={30} strokeWidth={1.2} /></div>}
    <div style={{ fontFamily: S.serif, fontSize: 24, fontWeight: 500, color: S.ink, lineHeight: 1.25 }}>{title}</div>
    {description && <p style={{ margin: '8px auto 0', fontSize: 13.5, lineHeight: 1.75, maxWidth: 420 }}>{description}</p>}
    {action && <div style={{ marginTop: 18 }}>{action}</div>}
  </div>
);

// Numbered "how it works" steps, e.g. for the affiliate programme.
export const Steps = ({ steps = [] }) => (
  <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
    {steps.map((st, i) => (
      <li key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', background: S.card, border: `1px solid ${S.lineSoft}`, borderRadius: S.radius, padding: '16px' }}>
        <span style={{ flexShrink: 0, width: 30, height: 30, borderRadius: '50%', border: `1px solid ${S.ink}`, color: S.ink, fontFamily: S.serif, fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: S.ink }}>{st.title}</div>
          {st.text && <div style={{ marginTop: 3, fontSize: 13, color: S.sub, lineHeight: 1.6 }}>{st.text}</div>}
        </div>
      </li>
    ))}
  </ol>
);

// Small status label — a tinted, square-cornered, letter-spaced tag.
export const Pill = ({ tone = 'neutral', children }) => {
  const map = {
    ok: ['rgba(63,125,90,.12)', S.ok], warn: ['rgba(147,89,26,.10)', S.warn], bad: ['rgba(158,43,37,.09)', S.bad],
    brand: ['rgba(138,109,59,.12)', S.gold], neutral: [S.soft, S.ink2],
  };
  const [bg, fg] = map[tone] || map.neutral;
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: S.radius, background: bg, color: fg, fontSize: 11.5, fontWeight: 500, letterSpacing: '.12em', whiteSpace: 'nowrap' }}>{children}</span>;
};

// Page wrapper: consistent width and padding for simplified pages.
export const Page = ({ children, max = 1180, style }) => (
  <div style={{ maxWidth: max, margin: '0 auto', padding: '8px 4px 40px', boxSizing: 'border-box', ...style }}>{children}</div>
);
